// src/pages/admin/deliverySettings.tsx - Updated with full date and time
import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import { Edit, ArrowBack, InfoOutlined } from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useGetDeliverySettingsQuery,
  useUpdateDeliverySettingsMutation,
  DeliverySettings,
  UpdateDeliverySettingsRequest,
} from '../../features/admin/deliverySettingsApi.ts';

interface DeliveryFormData {
  daily_target: number;
  weekly_target: number;
  monthly_target: number;
  otp_price_limit: number;
  ontime_delivery_fallback: number;
  max_capacity: number;
}

interface DeliveryFormErrors {
  [key: string]: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const Delivery: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ 
    open: false, 
    message: '', 
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [deliveryId, setDeliveryId] = useState<number>(1);
  const [createdAt, setCreatedAt] = useState<string>('');
  const [formData, setFormData] = useState<DeliveryFormData>({
    daily_target: 0,
    weekly_target: 0,
    monthly_target: 0,
    otp_price_limit: 0,
    ontime_delivery_fallback: 0,
    max_capacity: 0,
  });
  const [formErrors, setFormErrors] = useState<DeliveryFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const { 
    data: deliveryResponse, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useGetDeliverySettingsQuery();

  const [updateDeliverySettings] = useUpdateDeliverySettingsMutation();

  const deliverySettings = deliveryResponse?.data;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const resetForm = () => {
    setFormData({
      daily_target: 0,
      weekly_target: 0,
      monthly_target: 0,
      otp_price_limit: 0,
      ontime_delivery_fallback: 0,
      max_capacity: 0,
    });
    setFormErrors({});
  };

  useEffect(() => {
    if (deliverySettings && !showForm) {
      setFormData({
        daily_target: deliverySettings.daily_target || 0,
        weekly_target: deliverySettings.weekly_target || 0,
        monthly_target: deliverySettings.monthly_target || 0,
        otp_price_limit: deliverySettings.otp_price_limit || 0,
        ontime_delivery_fallback: deliverySettings.ontime_delivery_fallback || 0,
        max_capacity: deliverySettings.max_capacity || 0,
      });
      setDeliveryId(deliverySettings.id);
      setCreatedAt(deliverySettings.created_at);
    }
  }, [deliverySettings, showForm]);

  const handleEditDeliverySettings = (settings: DeliverySettings) => {
    setFormData({
      daily_target: settings.daily_target || 0,
      weekly_target: settings.weekly_target || 0,
      monthly_target: settings.monthly_target || 0,
      otp_price_limit: settings.otp_price_limit || 0,
      ontime_delivery_fallback: settings.ontime_delivery_fallback || 0,
      max_capacity: settings.max_capacity || 0,
    });
    setDeliveryId(settings.id);
    setCreatedAt(settings.created_at);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: Number(value) || 0 }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: DeliveryFormErrors = {};
    
    if (formData.daily_target < 0) {
      newErrors.daily_target = 'Daily target cannot be negative';
    }
    
    if (formData.weekly_target < 0) {
      newErrors.weekly_target = 'Weekly target cannot be negative';
    }
    
    if (formData.monthly_target < 0) {
      newErrors.monthly_target = 'Monthly target cannot be negative';
    }
    
    if (formData.otp_price_limit < 0) {
      newErrors.otp_price_limit = 'OTP price limit cannot be negative';
    }
    
    if (formData.ontime_delivery_fallback < 0) {
      newErrors.ontime_delivery_fallback = 'On-time delivery fallback cannot be negative';
    }
    
    if (formData.max_capacity <= 0) {
      newErrors.max_capacity = 'Max capacity must be greater than 0';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsFormSubmitting(true);

    try {
      const requestData: UpdateDeliverySettingsRequest = {
        id: deliveryId,
        daily_target: formData.daily_target,
        weekly_target: formData.weekly_target,
        monthly_target: formData.monthly_target,
        otp_price_limit: formData.otp_price_limit,
        ontime_delivery_fallback: formData.ontime_delivery_fallback,
        max_capacity: formData.max_capacity,
        created_at: createdAt,
      };

      const result = await updateDeliverySettings(requestData).unwrap();
      
      setSnackbar({
        open: true,
        message: result.message || 'Delivery settings updated successfully!',
        severity: 'success'
      });
      
      // Wait before refetching to ensure backend has updated
      setTimeout(() => {
        refetch();
      }, 500);
      
      handleBackToList();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.message || error?.message || 'Error updating delivery settings.',
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      
      const date = new Date(dateString.replace(' ', 'T'));
      const formattedDate = date.toLocaleDateString('en-GB', { 
        day: '2-digit',
        month: 'short', 
        year: 'numeric',
      });
      
      const formattedTime = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
      
      return `${formattedDate}, ${formattedTime}`;
    } catch (error) {
      console.error('Date parsing error:', error);
      return dateString;
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  const getColumnHeaders = () => {
    if (isSmallScreen) {
      return ['Targets', 'Config', 'Capacity', 'Updated', 'Actions'];
    }
    return ['ID', 'Daily', 'Weekly', 'Monthly', 'OTP Limit', 'Fallback', 'Max Capacity', 'Updated', 'Actions'];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Delivery Settings
                  </Typography>
                  {deliverySettings && (
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => handleEditDeliverySettings(deliverySettings)}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      Edit Settings
                    </Button>
                  )}
                </Box>

                <Card sx={{ 
                  borderRadius: '16px', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
                  border: '1px solid #e1e3e5', 
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load delivery settings. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && !deliverySettings && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No delivery settings found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configure your delivery settings to manage targets and capacity
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && deliverySettings && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      <TableContainer>
                        <Table sx={{ minWidth: isSmallScreen ? 600 : 1200 }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#fafbfb' }}>
                              {getColumnHeaders().map((header, index) => (
                                <TableCell 
                                  key={index}
                                  sx={{ 
                                    color: '#6d7175', 
                                    fontWeight: 600, 
                                    fontSize: '11px', 
                                    textTransform: 'uppercase',
                                    borderBottom: '1px solid #e1e3e5',
                                    py: 1.5,
                                    px: 2
                                  }}
                                >
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                              {!isSmallScreen ? (
                                <>
                                  <TableCell sx={{ color: '#6d7175', fontWeight: 500, px: 2, py: 2 }}>
                                    {deliverySettings.id}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: '#202223', px: 2, py: 2 }}>
                                    {formatNumber(deliverySettings.daily_target)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', px: 2, py: 2 }}>
                                    {formatNumber(deliverySettings.weekly_target)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', px: 2, py: 2 }}>
                                    {formatNumber(deliverySettings.monthly_target)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', px: 2, py: 2 }}>
                                    {formatNumber(deliverySettings.otp_price_limit)}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', px: 2, py: 2 }}>
                                    {formatNumber(deliverySettings.ontime_delivery_fallback)}
                                  </TableCell>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Chip 
                                      label={formatNumber(deliverySettings.max_capacity)} 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: '#e3f2fd', 
                                        color: '#1976d2', 
                                        fontWeight: 600, 
                                        fontSize: '12px' 
                                      }} 
                                    />
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '13px', px: 2, py: 2 }}>
                                    {formatDate(deliverySettings.updated_at || deliverySettings.created_at)}
                                  </TableCell>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Tooltip title="Edit settings">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditDeliverySettings(deliverySettings)}
                                        sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </>
                              ) : (
                                // Compact view for small screens
                                <>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Stack spacing={0.5}>
                                      <Typography variant="caption" color="#6d7175">Daily</Typography>
                                      <Typography variant="body2" fontWeight={500}>
                                        {formatNumber(deliverySettings.daily_target)}
                                      </Typography>
                                      <Typography variant="caption" color="#6d7175">Weekly</Typography>
                                      <Typography variant="body2">
                                        {formatNumber(deliverySettings.weekly_target)}
                                      </Typography>
                                      <Typography variant="caption" color="#6d7175">Monthly</Typography>
                                      <Typography variant="body2">
                                        {formatNumber(deliverySettings.monthly_target)}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Stack spacing={0.5}>
                                      <Typography variant="caption" color="#6d7175">OTP Limit</Typography>
                                      <Typography variant="body2">
                                        {formatNumber(deliverySettings.otp_price_limit)}
                                      </Typography>
                                      <Typography variant="caption" color="#6d7175">Fallback</Typography>
                                      <Typography variant="body2">
                                        {formatNumber(deliverySettings.ontime_delivery_fallback)}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Chip 
                                      label={formatNumber(deliverySettings.max_capacity)} 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: '#e3f2fd', 
                                        color: '#1976d2', 
                                        fontWeight: 600, 
                                        fontSize: '12px' 
                                      }} 
                                    />
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '13px', px: 2, py: 2 }}>
                                    {formatDate(deliverySettings.updated_at || deliverySettings.created_at)}
                                  </TableCell>
                                  <TableCell sx={{ px: 2, py: 2 }}>
                                    <Tooltip title="Edit settings">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditDeliverySettings(deliverySettings)}
                                        sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Card>

                {deliverySettings && (
                  <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Card sx={{ 
                      flex: 1, 
                      minWidth: { xs: '100%', sm: '200px' },
                      borderRadius: '12px', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
                      border: '1px solid #e1e3e5',
                      p: 2
                    }}>
                      <Typography variant="caption" color="#6d7175" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <InfoOutlined fontSize="inherit" />
                        Daily Target
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {deliverySettings.daily_target?.toLocaleString()}
                      </Typography>
                    </Card>
                    
                    <Card sx={{ 
                      flex: 1, 
                      minWidth: { xs: '100%', sm: '200px' },
                      borderRadius: '12px', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
                      border: '1px solid #e1e3e5',
                      p: 2
                    }}>
                      <Typography variant="caption" color="#6d7175" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <InfoOutlined fontSize="inherit" />
                        Weekly Target
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {deliverySettings.weekly_target?.toLocaleString()}
                      </Typography>
                    </Card>
                    
                    <Card sx={{ 
                      flex: 1, 
                      minWidth: { xs: '100%', sm: '200px' },
                      borderRadius: '12px', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
                      border: '1px solid #e1e3e5',
                      p: 2
                    }}>
                      <Typography variant="caption" color="#6d7175" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <InfoOutlined fontSize="inherit" />
                        Max Capacity
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {deliverySettings.max_capacity?.toLocaleString()}
                      </Typography>
                    </Card>
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Delivery Settings
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Edit delivery settings
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                          Target Settings
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            name="daily_target"
                            label="Daily Target"
                            type="number"
                            value={formData.daily_target}
                            onChange={handleInputChange}
                            error={!!formErrors.daily_target}
                            helperText={formErrors.daily_target || 'Number of deliveries per day'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 0 }}
                            size={isMobile ? "small" : "medium"}
                          />

                          <TextField
                            name="weekly_target"
                            label="Weekly Target"
                            type="number"
                            value={formData.weekly_target}
                            onChange={handleInputChange}
                            error={!!formErrors.weekly_target}
                            helperText={formErrors.weekly_target || 'Number of deliveries per week'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 0 }}
                            size={isMobile ? "small" : "medium"}
                          />

                          <TextField
                            name="monthly_target"
                            label="Monthly Target"
                            type="number"
                            value={formData.monthly_target}
                            onChange={handleInputChange}
                            error={!!formErrors.monthly_target}
                            helperText={formErrors.monthly_target || 'Number of deliveries per month'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 0 }}
                            size={isMobile ? "small" : "medium"}
                          />
                        </Stack>
                      </Box>
                    </Card>

                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                          Delivery Configuration
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            name="otp_price_limit"
                            label="OTP Price Limit"
                            type="number"
                            value={formData.otp_price_limit}
                            onChange={handleInputChange}
                            error={!!formErrors.otp_price_limit}
                            helperText={formErrors.otp_price_limit || 'Maximum price for OTP-based deliveries'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 0 }}
                            size={isMobile ? "small" : "medium"}
                          />

                          <TextField
                            name="ontime_delivery_fallback"
                            label="On-Time Delivery Fallback"
                            type="number"
                            value={formData.ontime_delivery_fallback}
                            onChange={handleInputChange}
                            error={!!formErrors.ontime_delivery_fallback}
                            helperText={formErrors.ontime_delivery_fallback || 'Fallback time in minutes for on-time delivery'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 0 }}
                            size={isMobile ? "small" : "medium"}
                          />

                          <TextField
                            name="max_capacity"
                            label="Maximum Capacity"
                            type="number"
                            value={formData.max_capacity}
                            onChange={handleInputChange}
                            error={!!formErrors.max_capacity}
                            helperText={formErrors.max_capacity || 'Maximum number of concurrent deliveries'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            inputProps={{ min: 1 }}
                            size={isMobile ? "small" : "medium"}
                          />
                        </Stack>
                      </Box>
                    </Card>
                  </Stack>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          saving...
                        </Box>
                      ) : (
                        'Save settings'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Delivery;